import React, { useEffect, useState } from 'react';
import { Col, Row, getCurrentDayWeekNumber } from '../../../lib/utils';
import { Body1Bold, Body2Bold, Body2Regular, Body3Regular } from '../../../lib/font';
import { I_LEFT_ANGLE, I_RIGHT_ANGLE } from '../../../types/images';
import { colors } from '../../../lib/colors';
import { useQuery } from '@tanstack/react-query';
import { getMembershipProgressAPI } from '../../../api/dashboard';
import dayjs from 'dayjs';
import MemberShipTodayProgress from './MemberShipTodayProgress';
import MemberShipWeekProgress from './MemberShipWeekProgress';
import MemberShipMonthProgress from './MemberShipMonthProgress';
import { dashBoardMemberShipProgressDummyData } from '../../../lib/dummyData';
import ThreeButton from '../../Button/ThreeButton';
import ResetButton from '../../Button/ResetButton';

const DashBoardMemberShipProgress = ({ authorization, authObject }: any) => {
    // 초기값 설정
    const [currentDate, setCurrentDate] = useState(dayjs());
    const [todayData, setTodayData] = useState<any>(null);
    const [thisWeekData, setThisWeekData] = useState<any>(null);
    const [thisMonthData, setThisMonthData] = useState<any>(null);

    const [todayMaxCount, setTodayMaxCount] = useState<number>(0);
    const [weekMaxCount, setWeekMaxCount] = useState<number>(0);
    const [monthMaxCount, setMonthMaxCount] = useState<number>(0);

    // 신규 수강권수 합
    const [todayTotalCount, setTodayTotalCount] = useState<number>(0);
    const [weekTotalCount, setWeekTotalCount] = useState<number>(0);
    const [monthTotalCount, setMonthTotalCount] = useState<number>(0);

    // 몇주차 표시
    const [weekNumber, setWeekNumber] = useState<number>(0);

    const [isToday, setIsToday] = useState<boolean>(false);
    const [isWeek, setIsWeek] = useState<boolean>(true);
    const [isMonth, setIsMonth] = useState<boolean>(false);

    /** 페이징 처리 */
    const pageSize = 10;
    /** 당일 */
    const [currentTodayPage, setCurrentTodayPage] = useState<number>(1);
    const startTodayIndex = (currentTodayPage - 1) * pageSize;
    const endTodayIndex = startTodayIndex + pageSize;
    let todayCurrentData = todayData && todayData.slice(startTodayIndex, endTodayIndex);
    let endTodayPage: number = 0;
    endTodayPage = todayData && Math.ceil(todayData.length / pageSize);

    /** 이번 주 */
    const [currentWeekPage, setCurrentWeekPage] = useState<number>(1);
    const startWeekIndex = (currentWeekPage - 1) * pageSize;
    const endWeekIndex = startWeekIndex + pageSize;
    let weekCurrentData = thisWeekData && thisWeekData.slice(startWeekIndex, endWeekIndex);
    let endWeekPage: number = thisWeekData && Math.ceil(thisWeekData.length / pageSize);

    /** 이번 달 */
    const [currentMonthPage, setCurrentMonthPage] = useState<number>(1);
    const startMonthIndex = (currentMonthPage - 1) * pageSize;
    const endMonthIndex = startMonthIndex + pageSize;
    let monthCurrentData = thisMonthData && thisMonthData.slice(startMonthIndex, endMonthIndex);
    let endMonthPage: number = thisMonthData && Math.ceil(thisMonthData.length / pageSize);

    const handleNextPage = () => {
        if (isToday) {
            if (currentTodayPage < Math.ceil(todayData.length / pageSize)) {
                setCurrentTodayPage(currentTodayPage + 1);
            }
        }

        if (isWeek) {
            if (currentWeekPage < Math.ceil(thisWeekData.length / pageSize)) {
                setCurrentWeekPage(currentWeekPage + 1);
            }
        }

        if (isMonth) {
            if (currentMonthPage < Math.ceil(thisMonthData.length / pageSize)) {
                setCurrentMonthPage(currentMonthPage + 1);
            }
        }
    };

    const handlePrevPage = () => {
        if (isToday) {
            if (currentTodayPage > 1) {
                setCurrentTodayPage(currentTodayPage - 1);
            }
        }

        if (isWeek) {
            if (currentWeekPage > 1) {
                setCurrentWeekPage(currentWeekPage - 1);
            }
        }

        if (isMonth) {
            if (currentMonthPage > 1) {
                setCurrentMonthPage(currentMonthPage - 1);
            }
        }
    };

    const handleResetPage = () => {
        setCurrentTodayPage(1);
        setCurrentWeekPage(1);
        setCurrentMonthPage(1);
    };

    const settingDate = (type: string) => {
        handleResetPage();
        setCurrentDate(dayjs());
        if (type === 'today') {
            setIsToday(true);
            setIsWeek(false);
            setIsMonth(false);
        }

        if (type === 'thisWeek') {
            setIsToday(false);
            setIsWeek(true);
            setIsMonth(false);
        }

        if (type === 'thisMonth') {
            setIsToday(false);
            setIsWeek(false);
            setIsMonth(true);
        }
    };

    const onClickAddDate = () => {
        if (isToday) {
            const newDate = dayjs(currentDate).add(1, 'day');
            setCurrentDate(newDate);
            handleResetPage();
        }

        if (isWeek) {
            const newDate = dayjs(currentDate).add(1, 'week');
            setCurrentDate(newDate);
            handleResetPage();
        }

        if (isMonth) {
            const newDate = dayjs(currentDate).add(1, 'month');
            setCurrentDate(newDate);
            handleResetPage();
        }
    };

    const onClickSubtractDate = () => {
        if (isToday) {
            const newDate = dayjs(currentDate).subtract(1, 'day');
            setCurrentDate(newDate);
            handleResetPage();
        }

        if (isWeek) {
            const newDate = dayjs(currentDate).subtract(1, 'week');
            setCurrentDate(newDate);
            handleResetPage();
        }

        if (isMonth) {
            const newDate = dayjs(currentDate).subtract(1, 'month');
            setCurrentDate(newDate);
            handleResetPage();
        }
    };

    useEffect(() => {
        if (currentDate) {
            setWeekNumber(getCurrentDayWeekNumber(currentDate));
        }
    }, [currentDate]);

    //  수강권 등록 조회 API : (GET)
    const getMembershipProgress = useQuery(
        ['getMembershipProgressAPI', currentDate],
        async () =>
            await getMembershipProgressAPI(
                authObject?.centerId,
                dayjs(currentDate).format('YYYY-MM-DD'),
                authorization
            ),
        {
            onSuccess: (res) => {
                if (res.status === 200) {
                    const thisDay = res.data.thisDay;
                    const thisWeek = res.data.thisWeek;
                    const thisMonth = res.data.thisMonth;

                    const thisDayMaxCount = res.data.thisDayMaxCount;
                    const thisWeekMaxCount = res.data.thisWeekMaxCount;
                    const thisMonthMaxCount = res.data.thisMonthMaxCount;

                    setTodayData(thisDay);
                    setThisWeekData(thisWeek);
                    setThisMonthData(thisMonth);

                    setTodayMaxCount(thisDayMaxCount);
                    setWeekMaxCount(thisWeekMaxCount);
                    setMonthMaxCount(thisMonthMaxCount);
                }
            },
            onError: (err) => {
                console.log(err);
            },
            enabled: !!authorization && !!!authObject?.testAccount,
            refetchOnWindowFocus: 'always',
            staleTime: 0,
        }
    );

    useEffect(() => {
        if (todayData && todayData.length > 0) {
            let totalArr = [];
            for (let i = 0; i < todayData.length; i++) {
                totalArr.push(todayData[i].newMembershipCount);
            }

            const totalSum = totalArr.reduce((accumulator, currentValue) => accumulator + currentValue, 0);
            setTodayTotalCount(totalSum);
        }

        if (thisWeekData && thisWeekData.length > 0) {
            let totalArr = [];
            for (let i = 0; i < thisWeekData.length; i++) {
                totalArr.push(thisWeekData[i].newMembershipCount);
            }
            const totalSum = totalArr.reduce((accumulator, currentValue) => accumulator + currentValue, 0);
            setWeekTotalCount(totalSum);
        }

        if (thisMonthData && thisMonthData.length > 0) {
            let totalArr = [];
            for (let i = 0; i < thisMonthData.length; i++) {
                totalArr.push(thisMonthData[i].newMembershipCount);
            }
            const totalSum = totalArr.reduce((accumulator, currentValue) => accumulator + currentValue, 0);
            setMonthTotalCount(totalSum);
        }
    }, [todayData, thisWeekData, thisMonthData]);

    // 더미데이터 세팅
    // useEffect(() => {
    //     if (authObject?.testAccount && dashBoardMemberShipProgressDummyData) {
    //         const thisDay = dashBoardMemberShipProgressDummyData.thisDay;
    //         const thisWeek = dashBoardMemberShipProgressDummyData.thisWeek;
    //         const thisMonth = dashBoardMemberShipProgressDummyData.thisMonth;

    //         const thisDayMaxCount = dashBoardMemberShipProgressDummyData.thisDayMaxCount;
    //         const thisWeekMaxCount = dashBoardMemberShipProgressDummyData.thisWeekMaxCount;
    //         const thisMonthMaxCount = dashBoardMemberShipProgressDummyData.thisMonthMaxCount;

    //         setTodayData(thisDay);
    //         setThisWeekData(thisWeek);
    //         setThisMonthData(thisMonth);

    //         setTodayMaxCount(thisDayMaxCount);
    //         setWeekMaxCount(thisWeekMaxCount);
    //         setMonthMaxCount(thisMonthMaxCount);
    //     }
    // }, [dashBoardMemberShipProgressDummyData]);

    // 그래프에서 마우스 호버시에 나오는 툴팁 커스텀 디자인
    const CustomToolTip = ({ id, value, color, data }: any) => (
        <Col
            style={{
                width: 162,
                height: 96,
                background: 'rgba(20, 20, 23, 0.46)',
                borderRadius: '16px 16px 16px 16px',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Row style={{ width: 96, alignItems: 'center' }}>
                <Row style={{ width: 16, height: 16, borderRadius: 2, backgroundColor: colors.BLUE_500 }} />
                <Body2Regular style={{ marginLeft: 8, color: colors.WHITE_100 }}>
                    {'신규 :'}
                    <Body2Bold>{data.newMembershipCount}</Body2Bold>
                </Body2Regular>
            </Row>
        </Col>
    );
    // 그래프에서 그려지는 막대그래프에 나오는 숫자 커스텀 디자인
    const CustomTextLayer = ({ bars }: any) => {
        return (
            <g>
                {bars.map((bar: any, index: number) => (
                    <text
                        key={index}
                        x={bar.x + bar.width / 2}
                        y={320}
                        textAnchor="middle"
                        style={{
                            fill: 'white',
                            fontSize: 16,
                            fontWeight: 700,
                            //lineHeight: 24,
                        }}
                    >
                        {bar.data.value > 0 && bar.data.value}
                    </text>
                ))}
            </g>
        );
    };

    return (
        <Col
            style={{
                width: 1192,
                height: 640,
                marginTop: 0,
                borderRadius: 28,
                background: 'linear-gradient(180deg, #FFFFFF 0%, #F8FBFF 100%)',
                border: '1px solid #DCE7F5',
                boxShadow: '0 20px 42px rgba(15,23,42,0.05)',
                overflow: 'hidden',
            }}
        >
            <Col style={{ marginTop: 34, marginLeft: 36, gap: 8 }}>
                <div style={{ fontSize: 28, fontWeight: 800, color: '#111827', letterSpacing: -0.8 }}>수강권 등록</div>
                <Body3Regular style={{ color: '#64748B' }}>
                    기간별 신규 수강권 발급 흐름을 보고 등록 추세를 빠르게 확인하세요.
                </Body3Regular>
            </Col>

            <Row
                style={{
                    marginTop: 22,
                    alignItems: 'center',
                    marginLeft: 36,
                    justifyContent: 'space-between',
                    paddingRight: 36,
                }}
            >
                <Row style={{ alignItems: 'center' }}>
                    {/* 날짜 버튼 */}
                    <Row
                        style={{
                            width: 250,
                            height: 46,
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginRight: 24,
                            padding: '0 10px',
                            borderRadius: 16,
                            backgroundColor: '#F8FAFC',
                            border: '1px solid #E2E8F0',
                        }}
                    >
                        <button
                            style={{
                                width: 28,
                                height: 28,
                                borderRadius: 999,
                                backgroundColor: '#FFFFFF',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 4px 10px rgba(15,23,42,0.05)',
                                border: 'none',
                                cursor: 'pointer',
                            }}
                            onClick={() => onClickSubtractDate()}
                        >
                            <img src={I_LEFT_ANGLE} style={{ width: 24, height: 24 }} />
                        </button>
                        <Body3Regular style={{ width: 160, textAlign: 'center', color: '#334155', fontWeight: 700 }}>
                            {isToday
                                ? currentDate.format('YYYY년 MM월 DD일 (ddd)')
                                : isWeek
                                ? currentDate.format('MM월 ') + weekNumber + '주차'
                                : currentDate.format('YYYY년 MM월')}
                        </Body3Regular>
                        <button
                            style={{
                                width: 28,
                                height: 28,
                                borderRadius: 999,
                                backgroundColor: '#FFFFFF',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 4px 10px rgba(15,23,42,0.05)',
                                border: 'none',
                                cursor: 'pointer',
                            }}
                            onClick={() => onClickAddDate()}
                        >
                            <img src={I_RIGHT_ANGLE} style={{ width: 24, height: 24 }} />
                        </button>
                    </Row>

                    {/* 일간 / 주간 / 월간 버튼 */}
                    <ThreeButton
                        isFirst={isToday}
                        isSecond={isWeek}
                        isThird={isMonth}
                        firstText={'일간'}
                        secondText={'주간'}
                        thirdText={'월간'}
                        firstCallBack={() => settingDate('today')}
                        secondCallBack={() => settingDate('thisWeek')}
                        thirdCallBack={() => settingDate('thisMonth')}
                    />

                    <div
                        style={{
                            marginLeft: 16,
                            padding: '8px 14px',
                            borderRadius: 12,
                            border: '1px solid #E2E8F0',
                            backgroundColor: '#FFFFFF',
                        }}
                    >
                        <ResetButton style={{ marginLeft: 0 }} callBack={() => setCurrentDate(dayjs())} />
                    </div>
                </Row>

                <Row
                    style={{
                        height: 38,
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '0 14px',
                        borderRadius: 999,
                        backgroundColor: '#EFF6FF',
                        border: '1px solid #BFDBFE',
                    }}
                >
                    <div
                        style={{
                            width: 12,
                            height: 12,
                            borderRadius: 999,
                            backgroundColor: colors.PRIMARY_BLUE,
                            marginRight: 8,
                        }}
                    />
                    <Body3Regular style={{ color: colors.LAVEL_3 }}>{`신규 수강권 수(${
                        isToday ? todayTotalCount : isWeek ? weekTotalCount : monthTotalCount
                    })`}</Body3Regular>
                </Row>
            </Row>

            <div
                style={{
                    width: 1192,
                    height: 614,
                    marginTop: 18,
                }}
            >
                {isToday && todayCurrentData && (
                    <MemberShipTodayProgress
                        todayCurrentData={todayCurrentData}
                        todayMaxCount={todayMaxCount}
                        handlePrevPage={handlePrevPage}
                        handleNextPage={handleNextPage}
                        currentTodayPage={currentTodayPage}
                        endTodayPage={endTodayPage}
                        CustomToolTip={CustomToolTip}
                        CustomTextLayer={CustomTextLayer}
                    />
                )}
                {isWeek && weekCurrentData && (
                    <MemberShipWeekProgress
                        weekCurrentData={weekCurrentData}
                        weekMaxCount={weekMaxCount}
                        handlePrevPage={handlePrevPage}
                        handleNextPage={handleNextPage}
                        currentWeekPage={currentWeekPage}
                        endWeekPage={endWeekPage}
                        CustomToolTip={CustomToolTip}
                        CustomTextLayer={CustomTextLayer}
                    />
                )}
                {isMonth && monthCurrentData && (
                    <MemberShipMonthProgress
                        monthCurrentData={monthCurrentData}
                        monthMaxCount={monthMaxCount}
                        handlePrevPage={handlePrevPage}
                        handleNextPage={handleNextPage}
                        currentMonthPage={currentMonthPage}
                        endMonthPage={endMonthPage}
                        CustomToolTip={CustomToolTip}
                        CustomTextLayer={CustomTextLayer}
                    />
                )}
            </div>
        </Col>
    );
};

export default DashBoardMemberShipProgress;
